import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { DatePicker, Form, Input, InputNumber, Space, Spin, Switch } from "antd";
import dayjs from "dayjs";
import { validateEmailCsv } from "../../../../swzutils/ValidationUtil";
import HelpLabel from "../../components/HelpLabel";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CustomerGroupSelect from "./CustomerGroupSelect";
import CustomerResource from "./api/CustomerResource";
import { axiosErrorHandler, showSuccessNotification } from "../../../../swzutils";
import CustomerDeliveryInstructionsFormItem from "./CustomerDeliveryInstructionsFormItem";
import AdminUserSelect from "../../components/AdminUserSelect";
import HasPermissions from "../../../../containers/HasPermissions";
const CustomerForm = ({ tenantId, customer, onSave }, ref) => {
    const [form] = Form.useForm();
    const [active, setActive] = useState(true);
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    useImperativeHandle(ref, () => ({
        submit() {
            form.submit();
        },
    }));
    const handleSubmit = (formValues) => {
        setNetworkRequestInProgress(true);
        const request = {
            tenantId: customer?.id ? customer.tenant.id : tenantId,
            code: formValues.customerCode,
            name: formValues.customerName,
            podEmailCsv: formValues.customerEmail,
            customerGroupId: formValues.customerGroupId,
            version: customer?.version,
            tier: formValues.tier,
            deliveryInstructions: formValues.instructions,
            salesAdminUserId: formValues?.salesAdminUserId || null,
            disabledAt: customer?.tenant.id ? (formValues.active ? null : formValues.disabledAt) : null
        };
        if (customer && customer?.id) {
            CustomerResource.update(customer.id, request, response => {
                showSuccessNotification(`Customer ${response.name} updated`);
                onSave(response);
            }, axiosErrorHandler, () => setNetworkRequestInProgress(false));
        }
        else {
            CustomerResource.create(request, response => {
                showSuccessNotification(`Customer ${response.name} created`);
                onSave(response);
            }, axiosErrorHandler, () => setNetworkRequestInProgress(false));
        }
    };
    useEffect(() => {
        if (customer) {
            populateCustomerFields(customer);
        }
        else {
            form.resetFields();
        }
    }, [customer]);
    const populateCustomerFields = (customer) => {
        const active = !customer.disabledAt;
        form.setFieldsValue({
            customerCode: customer.code,
            customerName: customer.name,
            customerGroupId: customer.group?.id,
            customerEmail: customer.podEmailCsv,
            tier: customer.tier,
            salesAdminUserId: customer?.salesPerson?.user?.id,
            active: active,
            disabledAt: customer.disabledAt ? dayjs(customer.disabledAt) : null
        });
        setActive(active);
    };
    return (_jsx(Spin, { spinning: networkRequestInProgress, children: _jsxs(Form, { layout: "vertical", form: form, onFinish: handleSubmit, children: [_jsx(Form.Item, { name: "customerCode", label: "Code", rules: [
                        { max: 50, message: 'Please limit to 50 characters.' }
                    ], children: _jsx(Input, { maxLength: 50 }) }), _jsx(Form.Item, { name: "customerName", label: "Name", rules: [
                        { required: true, message: 'Customer Name is required' },
                        { max: 100, message: 'Please limit to 100 characters.' }
                    ], children: _jsx(Input, { maxLength: 100 }) }), _jsx(Form.Item, { name: "customerEmail", label: _jsx(HelpLabel, { title: "Proof of Delivery Email Address", helpText: "At completion of a delivery the entered email addresses will be sent an email with a proof of delivery PDF attached. The PDF includes details of the delivery along with signature and photos (if requested)." }), extra: "Multiple email addresses can be separated with a comma", rules: [
                        { max: 500, message: 'Please limit the contact email from to 500 characters.' },
                        { validator: validateEmailCsv }
                    ], children: _jsx(Input, { maxLength: 500 }) }), _jsxs(Space, { children: [_jsx(Form.Item, { name: "tier", label: "Tier", children: _jsx(InputNumber, { min: 1, max: 3, step: 1, placeholder: "Tier 1-3" }) }), _jsx(Form.Item, { name: "customerGroupId", label: "Group", children: _jsx(CustomerGroupSelect, { tenantId: customer?.tenant.id }) })] }), customer?.id &&
                    _jsxs(_Fragment, { children: [_jsx(Form.Item, { name: "active", label: "Active", children: _jsx(Switch, { onChange: setActive, checked: active, checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}) }) }), _jsx(Form.Item, { name: "disabledAt", label: "Disable at", style: { display: active ? 'none' : 'inherit' }, rules: [{
                                        required: !active,
                                        message: 'Please select a Date / Time from which to disable this customer',
                                    }], children: _jsx(DatePicker, { showTime: true, allowClear: false, format: "DD-MM-YYYY HH:mm", placeholder: "Disable at" }) })] }), _jsx(HasPermissions, { permissions: ['admin:user:list', 'admin:user:salesAdminUser:list'], children: _jsx(Form.Item, { name: "salesAdminUserId", label: "Sales Person", rules: [
                            { required: true, message: 'Sales Person is required' }
                        ], children: _jsx(AdminUserSelect, { tenantId: customer?.tenant.id, allowClear: true, placeholder: "Sales Person", salesAdminUserEnabled: true, disabled: false }) }) }), _jsx(CustomerDeliveryInstructionsFormItem, { form: form, customerId: customer?.id })] }) }));
};
export default forwardRef(CustomerForm);
